import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import type { ActionAssigneeKind } from '../ICSConstants';
import { TicketCheckboxField } from './TicketCheckboxField';
import { TicketDateTimeField } from './TicketDateTimeField';
import { ResponsibleIndividualsField } from './ResponsibleIndividualsField';
import { TicketSelectField } from './TicketSelectField';
import { useTaskFieldEdit } from './TaskFieldEditContext';
import { useAorText } from 'utils/aorTerminology';
import { useHwcgFlag } from 'utils/featureFlags';

export type SelectOption = { label: string; value: string };

type ActionDetailsSidebarProps = {
  assigneeKind: ActionAssigneeKind;
  assigneeKindOptions: SelectOption[];
  pocOptions: SelectOption[];
  teamOptions: SelectOption[];
  positionOptions: SelectOption[];
  roleOptions?: SelectOption[];
  aorOptions: SelectOption[];
  aorTitle?: string;
  aorMulti?: boolean;
  /** Incident objectives this task can serve. Absent at org level. */
  objectiveOptions?: SelectOption[];
  showObjective?: boolean;
  /** Incident name in incident scope; blank at org level. */
  workspaceLabel?: string;
  dependencyChoices: SelectOption[];
  dependsOn: string[];
  onDependsOnChange: (next: string[]) => void;
  responsibleIndividualOptions?: SelectOption[];
  responsibleIndividualIds?: string[];
  onResponsibleIndividualIdsChange?: (next: string[]) => void;
  /** The selected ICS position id; the people picker stays hidden until set. */
  assigneeId?: string | null;
};

const DetailLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
    {children}
  </div>
);

/**
 * The ticket's DETAILS rail: who, where and when, kept out of the main column
 * so the body reads as the work itself.
 *
 * Two columns while the ticket is narrow (map open or mobile, where the rail
 * sits above the body) and a single stack once it moves alongside the body, so
 * nothing is ever cut off or needs horizontal scrolling.
 */
export const ActionDetailsSidebar: React.FC<ActionDetailsSidebarProps> = ({
  assigneeKind,
  assigneeKindOptions,
  pocOptions,
  teamOptions,
  positionOptions,
  roleOptions = [],
  aorOptions,
  aorTitle,
  aorMulti = false,
  objectiveOptions = [],
  showObjective = false,
  workspaceLabel,
  dependencyChoices,
  dependsOn,
  onDependsOnChange,
  responsibleIndividualOptions = [],
  responsibleIndividualIds = [],
  onResponsibleIndividualIdsChange,
  assigneeId,
}) => {
  const hwcg = useHwcgFlag();
  const aorText = useAorText();
  const { autoPersist, persistTip } = useTaskFieldEdit();
  return (
    <div className="tw:rounded-lg tw:border tw:border-border tw:bg-muted/20 tw:p-3">
      <DetailLabel>Details</DetailLabel>
      <div className="tw:mt-3 tw:grid tw:grid-cols-2 tw:gap-x-3 tw:gap-y-3 tw:lg:grid-cols-1">
        {assigneeKindOptions.length > 1 && (
          <TicketSelectField
            title="Assign To"
            fieldName="assignee_kind"
            options={assigneeKindOptions}
          />
        )}
        {assigneeKind === 'user' && (
          <TicketSelectField
            title="Assignee"
            fieldName="poc"
            options={pocOptions}
            isClearable
          />
        )}
        {assigneeKind === 'team' && (
          <TicketSelectField
            title="Team"
            fieldName="assignee_id"
            options={teamOptions}
            isClearable
          />
        )}
        {assigneeKind === 'position' && (
          <TicketSelectField
            title="ICS Position"
            fieldName="assignee_id"
            options={positionOptions}
            isClearable
          />
        )}
        {assigneeKind === 'role' && (
          <TicketSelectField
            title="Role"
            fieldName="assignee_id"
            options={roleOptions}
            isClearable
          />
        )}
        {assigneeKind === 'position' && onResponsibleIndividualIdsChange ? (
          <ResponsibleIndividualsField
            positionSelected={Boolean(assigneeId)}
            options={responsibleIndividualOptions}
            value={responsibleIndividualIds}
            onChange={(next) => {
              onResponsibleIndividualIdsChange?.(next);
              if (autoPersist) {
                void persistTip({ responsible_individual_ids: next });
              }
            }}
          />
        ) : null}
        {hwcg ? null : aorMulti ? (
          <ResponsibleAorField
            title={aorText(aorTitle ?? 'Responsible AOR')}
            options={aorOptions}
          />
        ) : (
          <TicketSelectField
            title={aorText(aorTitle ?? 'AOR')}
            fieldName="aor_id"
            options={aorOptions}
            isClearable
          />
        )}
        {showObjective && !hwcg ? (
          <TicketSelectField
            title="Objective"
            fieldName="parent_id"
            options={objectiveOptions}
            isClearable
            placeholder="In service of"
          />
        ) : null}
        <div className="tw:space-y-1.5">
          <DetailLabel>Workspace</DetailLabel>
          <div className="tw:text-sm tw:text-muted-foreground">
            {workspaceLabel || '—'}
          </div>
        </div>
        <TicketDateTimeField title="Scheduled Start" fieldName="start_date" />
        <TicketDateTimeField title="Scheduled End" fieldName="target_date" />
        <TicketCheckboxField title="Briefed Assignee" fieldName="briefed_poc" />
      </div>
      {!hwcg && dependencyChoices.length > 0 && (
        <div className="tw:mt-3 tw:space-y-1.5">
          <DetailLabel>Blocked By</DetailLabel>
          <MultiIdCombobox
            options={dependencyChoices}
            value={dependsOn}
            onChange={(next) => {
              onDependsOnChange(next);
              if (autoPersist) void persistTip({ depends_on: next });
            }}
            placeholder="Tasks that must close first"
          />
          <p className="tw:m-0 tw:text-[0.675rem] tw:text-muted-foreground">
            This task stays blocked until every task listed here is Closed.
          </p>
        </div>
      )}
    </div>
  );
};

function ResponsibleAorField({
  title,
  options,
}: {
  title: string;
  options: SelectOption[];
}) {
  const { control } = useFormContext();
  const { autoPersist, persistTip } = useTaskFieldEdit();
  return (
    <Controller
      control={control}
      name="aor_ids"
      render={({ field }) => (
        <div className="tw:space-y-1.5">
          <DetailLabel>{title}</DetailLabel>
          <MultiIdCombobox
            options={options}
            value={field.value ?? []}
            onChange={(next) => {
              field.onChange(next);
              if (autoPersist) void persistTip();
            }}
            placeholder="Search AORs…"
          />
        </div>
      )}
    />
  );
}
